import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Calendar as CalendarIcon, MapPin, Globe, Building2, Layers, Zap, Droplets, Wifi,
  Ruler, DollarSign, Palette, Upload, CheckCircle2, ArrowRight, ArrowLeft,
  User, Star, Settings, LayoutGrid, Package, Monitor, UtensilsCrossed,
  Box, Presentation, X, Plus, Minus, Image as ImageIcon,
  FileText, LogIn, UserPlus, Loader2, Info, Clock, Hash, Lightbulb,
  Volume2, Coffee, ShieldCheck, Wind, Anchor, Warehouse, Eye, Sofa,
  Globe2, Wand2,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useDropdown, useCities, useVenues } from "@/contexts/DropdownContext";
import { PublicNavbar, PublicFooter } from "@/components/PublicLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── DatePicker helper ─────────────────────────────────────────────────────────
function DatePickerField({ label, icon, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value) : undefined;
  const display = selected
    ? selected.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "Pick a date";

  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
        {icon && <span className="inline mr-1">{icon}</span>}{label}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button"
            className={`w-full flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left ${!value ? 'text-slate-400' : 'text-slate-900'} ${open ? 'border-blue-500 ring-2 ring-blue-500' : 'border-slate-200'}`}>
            <span className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-slate-400 shrink-0" />
              {display}
            </span>
            {value && (
              <X className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 shrink-0"
                onClick={(e) => { e.stopPropagation(); onChange(''); setOpen(false); }} />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" side="bottom">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(date) => {
              if (date) onChange(date.toISOString().split('T')[0]);
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SECTORS = [
  "Automotive","Construction","Consumer Goods","Electronics","Energy",
  "Fashion & Apparel","Food & Beverage","Healthcare","Industrial",
  "IT / Technology","Jewelry","Logistics & Shipping","Manufacturing",
  "Medical & Pharma","Sports & Fitness","Travel & Tourism","Other",
];

const STAND_TYPES = [
  {
    id: "island", label: "Island", openSides: 4,
    desc: "4 open sides — Center of the exhibition floor",
    color: "emerald",
    svg: (
      <svg viewBox="0 0 80 80" className="w-14 h-14">
        <rect x="15" y="15" width="50" height="50" rx="4" fill="#d1fae5" stroke="#10b981" strokeWidth="2.5"/>
        <line x1="15" y1="40" x2="0" y2="40" stroke="#10b981" strokeWidth="2" strokeDasharray="4 3"/>
        <line x1="65" y1="40" x2="80" y2="40" stroke="#10b981" strokeWidth="2" strokeDasharray="4 3"/>
        <line x1="40" y1="15" x2="40" y2="0" stroke="#10b981" strokeWidth="2" strokeDasharray="4 3"/>
        <line x1="40" y1="65" x2="40" y2="80" stroke="#10b981" strokeWidth="2" strokeDasharray="4 3"/>
        <text x="40" y="44" textAnchor="middle" fill="#065f46" fontSize="9" fontWeight="bold">4 OPEN</text>
      </svg>
    ),
  },
  {
    id: "peninsula", label: "Peninsula", openSides: 3,
    desc: "3 open sides — End of aisle",
    color: "blue",
    svg: (
      <svg viewBox="0 0 80 80" className="w-14 h-14">
        <rect x="15" y="15" width="50" height="50" rx="4" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2.5"/>
        <rect x="15" y="60" width="50" height="6" rx="2" fill="#93c5fd"/>
        <line x1="15" y1="40" x2="0" y2="40" stroke="#3b82f6" strokeWidth="2" strokeDasharray="4 3"/>
        <line x1="65" y1="40" x2="80" y2="40" stroke="#3b82f6" strokeWidth="2" strokeDasharray="4 3"/>
        <line x1="40" y1="15" x2="40" y2="0" stroke="#3b82f6" strokeWidth="2" strokeDasharray="4 3"/>
        <text x="40" y="44" textAnchor="middle" fill="#1e3a8a" fontSize="9" fontWeight="bold">3 OPEN</text>
      </svg>
    ),
  },
  {
    id: "corner", label: "Corner", openSides: 2,
    desc: "2 adjacent sides open — Corner position",
    color: "orange",
    svg: (
      <svg viewBox="0 0 80 80" className="w-14 h-14">
        <rect x="15" y="15" width="50" height="50" rx="4" fill="#ffedd5" stroke="#f97316" strokeWidth="2.5"/>
        <rect x="15" y="60" width="50" height="6" rx="2" fill="#fdba74"/>
        <rect x="60" y="15" width="6" height="50" rx="2" fill="#fdba74"/>
        <line x1="15" y1="40" x2="0" y2="40" stroke="#f97316" strokeWidth="2" strokeDasharray="4 3"/>
        <line x1="40" y1="15" x2="40" y2="0" stroke="#f97316" strokeWidth="2" strokeDasharray="4 3"/>
        <text x="40" y="44" textAnchor="middle" fill="#7c2d12" fontSize="9" fontWeight="bold">2 OPEN</text>
      </svg>
    ),
  },
  {
    id: "inline", label: "Inline", openSides: 1,
    desc: "1 side open — Standard inline position",
    color: "purple",
    svg: (
      <svg viewBox="0 0 80 80" className="w-14 h-14">
        <rect x="15" y="15" width="50" height="50" rx="4" fill="#f3e8ff" stroke="#a855f7" strokeWidth="2.5"/>
        <rect x="15" y="60" width="50" height="6" rx="2" fill="#d8b4fe"/>
        <rect x="60" y="15" width="6" height="50" rx="2" fill="#d8b4fe"/>
        <rect x="15" y="15" width="50" height="6" rx="2" fill="#d8b4fe"/>
        <line x1="15" y1="40" x2="0" y2="40" stroke="#a855f7" strokeWidth="2" strokeDasharray="4 3"/>
        <text x="40" y="44" textAnchor="middle" fill="#581c87" fontSize="9" fontWeight="bold">1 OPEN</text>
      </svg>
    ),
  },
];

const CEILING_TYPES = [
  { id: "open",    label: "Open Top",      icon: <Eye className="w-5 h-5"/>,       desc: "No ceiling — open top" },
  { id: "partial", label: "Partial Cover", icon: <Layers className="w-5 h-5"/>,    desc: "Partial ceiling" },
  { id: "full",    label: "Full Ceiling",  icon: <Warehouse className="w-5 h-5"/>, desc: "Fully enclosed ceiling" },
  { id: "led",     label: "LED Ceiling",   icon: <Lightbulb className="w-5 h-5"/>, desc: "LED-lit ceiling" },
];

const AV_OPTIONS = [
  { id: "led_wall",    label: "LED Video Wall",    icon: <Monitor className="w-4 h-4"/> },
  { id: "lcd_screens", label: "LCD Screens",        icon: <Monitor className="w-4 h-4"/> },
  { id: "projector",   label: "Projector",          icon: <Presentation className="w-4 h-4"/> },
  { id: "sound",       label: "Sound System",       icon: <Volume2 className="w-4 h-4"/> },
  { id: "interactive", label: "Interactive Kiosk",  icon: <Star className="w-4 h-4"/> },
  { id: "vr",          label: "VR / AR Station",    icon: <Layers className="w-4 h-4"/> },
];

const LIGHTING_OPTIONS = [
  { id: "ambient",  label: "Ambient / General", icon: <Lightbulb className="w-4 h-4"/> },
  { id: "spot",     label: "Spot Lights",        icon: <Zap className="w-4 h-4"/> },
  { id: "led_strip",label: "LED Strip",           icon: <Hash className="w-4 h-4"/> },
  { id: "backlit",  label: "Backlit Panel",       icon: <ImageIcon className="w-4 h-4"/> },
  { id: "dramatic", label: "Dramatic / Stage",    icon: <Star className="w-4 h-4"/> },
];

const DESIGN_STYLES = [
  { id: "modern",     label: "Modern",              emoji: "◆", desc: "Clean lines, minimal" },
  { id: "minimalist", label: "Minimalist",           emoji: "○", desc: "Less is more" },
  { id: "corporate",  label: "Corporate",            emoji: "▣", desc: "Professional, formal" },
  { id: "bold",       label: "Bold / Creative",      emoji: "★", desc: "Striking, attention-grabbing" },
  { id: "natural",    label: "Natural / Sustainable",emoji: "✿", desc: "Wood, green, eco" },
  { id: "luxury",     label: "Luxury / Premium",     emoji: "◈", desc: "High quality, prestige" },
];

const MATERIALS = [
  { id: "wood",         label: "Wood / MDF",           icon: <Box className="w-4 h-4"/> },
  { id: "aluminum",     label: "Aluminium",            icon: <Anchor className="w-4 h-4"/> },
  { id: "fabric",       label: "Stretch Fabric",       icon: <Sofa className="w-4 h-4"/> },
  { id: "glass",        label: "Glass",                icon: <Eye className="w-4 h-4"/> },
  { id: "acrylic",      label: "Acrylic / Plexiglass", icon: <Layers className="w-4 h-4"/> },
  { id: "concrete_look",label: "Concrete Look",        icon: <Building2 className="w-4 h-4"/> },
  { id: "stone",        label: "Stone / Marble",       icon: <Warehouse className="w-4 h-4"/> },
  { id: "printed_wall", label: "Printed Wall",         icon: <ImageIcon className="w-4 h-4"/> },
];

const COLOR_SWATCHES = [
  "#0f172a","#1e293b","#334155","#dc2626","#ea580c","#d97706",
  "#65a30d","#059669","#0891b2","#2563eb","#7c3aed","#db2777",
  "#ffffff","#f8fafc","#f1f5f9","#e2e8f0",
];

const BUDGET_RANGES = [
  { id: "under_10k", label: "< €10,000" },
  { id: "10_25k",    label: "€10,000 – €25,000" },
  { id: "25_50k",    label: "€25,000 – €50,000" },
  { id: "50_100k",   label: "€50,000 – €100,000" },
  { id: "100_200k",  label: "€100,000 – €200,000" },
  { id: "over_200k", label: "> €200,000" },
];

const STEPS = [
  { id: 1, key: "event",     label: "Event",    icon: CalendarIcon },
  { id: 2, key: "stand",     label: "Stand",    icon: LayoutGrid },
  { id: 3, key: "technical", label: "Technical",icon: Settings   },
  { id: 4, key: "features",  label: "Features", icon: Star       },
  { id: 5, key: "design",    label: "Design",   icon: Palette    },
  { id: 6, key: "files",     label: "Files",    icon: Upload     },
  { id: 7, key: "budget",    label: "Budget",   icon: DollarSign },
  { id: 8, key: "summary",   label: "Summary",  icon: CheckCircle2},
  { id: 9, key: "account",   label: "Account",  icon: User       },
];

const INITIAL = {
  event:     { showName:"", sector:"", venue:"", city:"", country:"", startDate:"", endDate:"", hallLocation:"" },
  stand:     { type:"", widthM:"", depthM:"", heightM:"", ceilingType:"open", doubleStorey:false },
  technical: { raisedFlooring:false, raisedMm:"", electrical:"standard", electricalKw:"", water:false, drains:false, compressedAir:false, rigging:false, internet:"none" },
  features:  { receptionDesk:false, receptionCount:1, meetingRooms:0, storage:false, kitchen:false, stage:false, displayZones:0, lighting:[], av:[] },
  design:    { style:"", primaryColors:[], secondaryColors:[], materials:[], mood:"", designNotes:"" },
  assets:    { logoName:"", logoData:null, brandGuidelinesName:"", floorPlanName:"", referenceImages:[], existingPhotos:[] },
  budget:    { design:"", build:"", currency:"EUR", deadline:"", multipleShows:false, notes:"" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const Toggle = ({ value, onChange, label, icon }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 transition-all ${
      value ? "border-blue-600 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
    }`}
  >
    <span className={value ? "text-blue-600" : "text-slate-400"}>{icon}</span>
    <span className="text-sm font-medium">{label}</span>
    <span className={`ml-auto w-10 h-5 rounded-full transition-colors flex items-center px-0.5 ${value ? "bg-blue-600" : "bg-slate-200"}`}>
      <span className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? "translate-x-5" : "translate-x-0"}`}/>
    </span>
  </button>
);

const MultiChip = ({ options, selected, onToggle }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(o => (
      <button
        key={o.id} type="button"
        onClick={() => onToggle(o.id)}
        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
          selected.includes(o.id)
            ? "border-blue-600 bg-blue-600 text-white shadow-sm"
            : "border-slate-200 bg-white text-slate-600 hover:border-blue-400"
        }`}
      >
        {o.icon}<span>{o.label}</span>
      </button>
    ))}
  </div>
);

const Counter = ({ value, onChange, label, min = 0, max = 10 }) => (
  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))} className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 hover:bg-slate-100">
        <Minus className="w-3 h-3"/>
      </button>
      <span className="w-6 text-center font-bold text-slate-900">{value}</span>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))} className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 hover:bg-slate-100">
        <Plus className="w-3 h-3"/>
      </button>
    </div>
  </div>
);

const FileZone = ({ label, accept, icon, value, onChange, multiple = false }) => {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
    if (!token) {
      onChange(multiple ? Array.from(files).map(f => f.name) : files[0].name);
      return;
    }
    setUploading(true);
    try {
      const uploadedPaths = [];
      for (const file of Array.from(files).slice(0, multiple ? 5 : 1)) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${backendUrl}/api/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          uploadedPaths.push(data.path || data.file_id || file.name);
        } else {
          uploadedPaths.push(file.name);
        }
      }
      onChange(multiple ? uploadedPaths : uploadedPaths[0]);
    } catch {
      onChange(multiple ? Array.from(files).map(f => f.name) : files[0].name);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className={`w-full rounded-xl border-2 border-dashed px-4 py-4 text-left transition-all hover:border-blue-400 hover:bg-blue-50 ${
          value ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-white"
        }`}
      >
        <div className="flex items-center gap-3">
          <span className={value ? "text-blue-600" : uploading ? "text-amber-500" : "text-slate-400"}>
            {uploading ? <Loader2 className="w-5 h-5 animate-spin"/> : icon}
          </span>
          {uploading ? (
            <p className="text-sm font-medium text-amber-700">Uploading...</p>
          ) : value ? (
            <div>
              <p className="text-sm font-medium text-blue-800">
                {Array.isArray(value) ? `${value.length} files uploaded` : (typeof value === 'string' && value.includes('/') ? value.split('/').pop() : value)}
              </p>
              <p className="text-xs text-blue-600">Click to replace</p>
            </div>
          ) : (
            <div>
              <p className="text-sm font-medium text-slate-700">Click to select a file</p>
              <p className="text-xs text-slate-400">{accept}</p>
            </div>
          )}
        </div>
      </button>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden"
        onChange={e => handleUpload(e.target.files)}
      />
    </div>
  );
};

// ─── Step Components ───────────────────────────────────────────────────────────

function StepEvent({ data, onChange }) {
  const u = (k, v) => onChange({ ...data, [k]: v });
  const { countries } = useDropdown();
  const { cities } = useCities(data.country);
  const { venues } = useVenues(data.country, data.city);

  const handleCountryChange = (e) => {
    onChange({ ...data, country: e.target.value, city: '', venue: '' });
  };
  const handleCityChange = (e) => {
    onChange({ ...data, city: e.target.value, venue: '' });
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Show / Event Name *</label>
          <input value={data.showName} onChange={e => u("showName", e.target.value)}
            placeholder="e.g. Hannover Messe 2025" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Industry / Sector *</label>
          <select value={data.sector} onChange={e => u("sector", e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white">
            <option value="">Select...</option>
            {SECTORS.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5"><MapPin className="inline w-3 h-3 mr-1"/>Country *</label>
          <select value={data.country} onChange={handleCountryChange} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white">
            <option value="">Select country...</option>
            {countries.map(c => (
              <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5"><MapPin className="inline w-3 h-3 mr-1"/>City *</label>
          {cities.length > 0 ? (
            <select value={data.city} onChange={handleCityChange} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white">
              <option value="">Select city...</option>
              {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          ) : (
            <input value={data.city} onChange={e => u("city", e.target.value)} placeholder="e.g. Hannover" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5"><Building2 className="inline w-3 h-3 mr-1"/>Exhibition Venue</label>
          {venues.length > 0 ? (
            <select value={data.venue} onChange={e => u("venue", e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white">
              <option value="">Select venue...</option>
              {venues.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
            </select>
          ) : (
            <input value={data.venue} onChange={e => u("venue", e.target.value)} placeholder="e.g. Messe Hannover, Hall 7" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5"><Hash className="inline w-3 h-3 mr-1"/>Stand No / Hall Location</label>
          <input value={data.hallLocation} onChange={e => u("hallLocation", e.target.value)} placeholder="e.g. Hall 7, Stand B23" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <DatePickerField
          label="Start Date"
          icon={<CalendarIcon className="inline w-3 h-3" />}
          value={data.startDate}
          onChange={(v) => u("startDate", v)}
        />
        <DatePickerField
          label="End Date"
          icon={<CalendarIcon className="inline w-3 h-3" />}
          value={data.endDate}
          onChange={(v) => u("endDate", v)}
        />
      </div>
    </div>
  );
}

function StepStand({ data, onChange }) {
  const u = (k, v) => onChange({ ...data, [k]: v });
  const sqm = (parseFloat(data.widthM) || 0) * (parseFloat(data.depthM) || 0);
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Stand Type *</p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {STAND_TYPES.map(t => (
            <button key={t.id} type="button" onClick={() => u("type", t.id)}
              className={`flex flex-col items-center gap-2 rounded-2xl border-2 p-4 transition-all ${
                data.type === t.id
                  ? `border-${t.color}-500 bg-${t.color}-50 ring-2 ring-${t.color}-300`
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              {t.svg}
              <span className="font-bold text-sm text-slate-900">{t.label}</span>
              <span className="text-xs text-slate-500 text-center">{t.desc}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label:"Width (m) *",      key:"widthM",  placeholder:"e.g. 6" },
          { label:"Depth (m) *",      key:"depthM",  placeholder:"e.g. 4" },
          { label:"Max Height (m)",   key:"heightM", placeholder:"e.g. 3.5" },
        ].map(f => (
          <div key={f.key}>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5"><Ruler className="inline w-3 h-3 mr-1"/>{f.label}</label>
            <input type="number" step="0.5" min="0" value={data[f.key]} onChange={e => u(f.key, e.target.value)}
              placeholder={f.placeholder} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
        ))}
        <div className="flex flex-col justify-end">
          <div className="rounded-xl border-2 border-blue-200 bg-blue-50 px-4 py-3 text-center">
            <p className="text-xs text-blue-600 font-semibold uppercase">Total Area</p>
            <p className="text-2xl font-black text-blue-800">{sqm > 0 ? sqm.toFixed(1) : "—"}</p>
            <p className="text-xs text-blue-500">m²</p>
          </div>
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3"><Warehouse className="inline w-3 h-3 mr-1"/>Ceiling Type</p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {CEILING_TYPES.map(c => (
            <button key={c.id} type="button" onClick={() => u("ceilingType", c.id)}
              className={`flex flex-col items-center gap-2 rounded-xl border-2 p-3 text-center transition-all ${
                data.ceilingType === c.id ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-200 bg-white hover:border-slate-300 text-slate-600"
              }`}
            >
              {c.icon}<span className="text-xs font-bold">{c.label}</span>
              <span className="text-xs text-slate-400">{c.desc}</span>
            </button>
          ))}
        </div>
      </div>
      <Toggle value={data.doubleStorey} onChange={v => u("doubleStorey", v)}
        label="Double-storey stand" icon={<Layers className="w-5 h-5"/>}/>
    </div>
  );
}

function StepTechnical({ data, onChange }) {
  const u = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <Toggle value={data.raisedFlooring}  onChange={v => u("raisedFlooring", v)}  label="Raised Flooring (Platform)"     icon={<Layers className="w-5 h-5"/>}/>
        <Toggle value={data.water}           onChange={v => u("water", v)}           label="Water Connection Required"      icon={<Droplets className="w-5 h-5"/>}/>
        <Toggle value={data.drains}          onChange={v => u("drains", v)}          label="Drainage Line"                  icon={<Droplets className="w-5 h-5"/>}/>
        <Toggle value={data.compressedAir}   onChange={v => u("compressedAir", v)}   label="Compressed Air Line"            icon={<Wind className="w-5 h-5"/>}/>
        <Toggle value={data.rigging}         onChange={v => u("rigging", v)}         label="Rigging Permission Required"    icon={<Anchor className="w-5 h-5"/>}/>
      </div>
      {data.raisedFlooring && (
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Platform Height (mm)</label>
          <input type="number" value={data.raisedMm} onChange={e => u("raisedMm", e.target.value)}
            placeholder="e.g. 100" className="w-full max-w-xs rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
      )}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2"><Zap className="inline w-3 h-3 mr-1"/>Electrical Requirement</p>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {[["standard","Standard (≤5kW)"],["medium","Medium (5–15kW)"],["high","High (>15kW)"],["custom","Custom / Specify"]].map(([id, lbl]) => (
            <button key={id} type="button" onClick={() => u("electrical", id)}
              className={`rounded-xl border-2 p-3 text-xs font-semibold transition-all ${data.electrical === id ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              {lbl}
            </button>
          ))}
        </div>
        {data.electrical === "custom" && (
          <input type="text" value={data.electricalKw} onChange={e => u("electricalKw", e.target.value)}
            placeholder="Specify kW or custom requirement" className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        )}
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2"><Wifi className="inline w-3 h-3 mr-1"/>Internet / Network</p>
        <div className="flex gap-2">
          {[["none","Not Needed"],["basic","Basic Wi-Fi"],["dedicated","Dedicated Line"]].map(([id, lbl]) => (
            <button key={id} type="button" onClick={() => u("internet", id)}
              className={`rounded-xl border-2 px-4 py-2.5 text-sm font-semibold transition-all ${data.internet === id ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              {lbl}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function StepFeatures({ data, onChange }) {
  const u = (k, v) => onChange({ ...data, [k]: v });
  const toggleArr = (key, id) => {
    const arr = data[key] || [];
    onChange({ ...data, [key]: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] });
  };
  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-2">
        <Toggle value={data.receptionDesk} onChange={v => u("receptionDesk", v)} label="Reception / Welcome Desk"       icon={<Sofa className="w-5 h-5"/>}/>
        <Toggle value={data.storage}       onChange={v => u("storage", v)}       label="Storage / Back Room"            icon={<Box className="w-5 h-5"/>}/>
        <Toggle value={data.kitchen}       onChange={v => u("kitchen", v)}       label="Kitchen / Pantry"               icon={<Coffee className="w-5 h-5"/>}/>
        <Toggle value={data.stage}         onChange={v => u("stage", v)}         label="Stage / Presentation Area"      icon={<Presentation className="w-5 h-5"/>}/>
      </div>
      <Counter value={data.meetingRooms}  onChange={v => u("meetingRooms", v)}  label="Number of Meeting Rooms"        max={5}/>
      <Counter value={data.displayZones}  onChange={v => u("displayZones", v)}  label="Product Display Zones"          max={10}/>
      {data.receptionDesk && (
        <Counter value={data.receptionCount} onChange={v => u("receptionCount", v)} label="Number of Reception Desks" min={1} max={5}/>
      )}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2"><Lightbulb className="inline w-3 h-3 mr-1"/>Lighting Preferences</p>
        <MultiChip options={LIGHTING_OPTIONS} selected={data.lighting} onToggle={id => toggleArr("lighting", id)}/>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2"><Monitor className="inline w-3 h-3 mr-1"/>Audio / Visual Equipment (AV)</p>
        <MultiChip options={AV_OPTIONS} selected={data.av} onToggle={id => toggleArr("av", id)}/>
      </div>
    </div>
  );
}

function StepDesign({ data, onChange }) {
  const u = (k, v) => onChange({ ...data, [k]: v });
  const toggleColor = (key, hex) => {
    const arr = data[key] || [];
    onChange({ ...data, [key]: arr.includes(hex) ? arr.filter(x => x !== hex) : arr.length < 4 ? [...arr, hex] : arr });
  };
  const toggleMat = (id) => {
    const arr = data.materials || [];
    onChange({ ...data, materials: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] });
  };
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Design Style *</p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {DESIGN_STYLES.map(s => (
            <button key={s.id} type="button" onClick={() => u("style", s.id)}
              className={`flex flex-col items-start gap-1 rounded-xl border-2 p-4 transition-all ${
                data.style === s.id ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
              }`}>
              <span className="text-2xl">{s.emoji}</span>
              <span className="font-bold text-sm text-slate-900">{s.label}</span>
              <span className="text-xs text-slate-400">{s.desc}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2"><Palette className="inline w-3 h-3 mr-1"/>Primary Colors (select up to 4)</p>
        <div className="flex flex-wrap gap-2">
          {COLOR_SWATCHES.map(hex => (
            <button key={hex} type="button" onClick={() => toggleColor("primaryColors", hex)}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                (data.primaryColors || []).includes(hex) ? "border-blue-500 scale-125 ring-2 ring-blue-300" : "border-slate-200 hover:scale-110"
              }`} style={{ backgroundColor: hex }} title={hex}/>
          ))}
          <div className="flex items-center gap-1 ml-2">
            <input type="color" className="w-8 h-8 rounded-full border border-slate-200 cursor-pointer p-0"
              onChange={e => toggleColor("primaryColors", e.target.value)}/>
            <span className="text-xs text-slate-400">Custom</span>
          </div>
        </div>
        {(data.primaryColors || []).length > 0 && (
          <div className="flex gap-2 mt-2">
            {data.primaryColors.map(c => (
              <div key={c} className="flex items-center gap-1 rounded-full bg-slate-100 pl-1 pr-2 py-0.5">
                <div className="w-4 h-4 rounded-full border border-slate-300" style={{ backgroundColor: c }}/>
                <span className="text-xs font-mono">{c}</span>
                <button onClick={() => toggleColor("primaryColors", c)} className="ml-1 text-slate-400 hover:text-red-500"><X className="w-3 h-3"/></button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Secondary / Accent Colors</p>
        <div className="flex flex-wrap gap-2">
          {COLOR_SWATCHES.map(hex => (
            <button key={hex} type="button" onClick={() => toggleColor("secondaryColors", hex)}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                (data.secondaryColors || []).includes(hex) ? "border-purple-500 scale-125 ring-2 ring-purple-300" : "border-slate-200 hover:scale-110"
              }`} style={{ backgroundColor: hex }}/>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Material Preferences</p>
        <MultiChip options={MATERIALS} selected={data.materials || []} onToggle={toggleMat}/>
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Additional Design Notes</label>
        <textarea value={data.designNotes} onChange={e => u("designNotes", e.target.value)}
          rows={3} placeholder="Reference brands, inspiration sources, special requests..."
          className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none"/>
      </div>
    </div>
  );
}

function StepAssets({ data, onChange }) {
  const u = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
        <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5"/>
        <p className="text-sm text-amber-700">Select files now — they will be uploaded when your brief is submitted. Designers will review your logo and references in detail.</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <FileZone label="Company Logo (SVG / PNG / AI)" accept=".svg,.png,.ai,.eps,.pdf,.jpg" icon={<Building2 className="w-5 h-5"/>}
          value={data.logoName} onChange={v => u("logoName", v)}/>
        <FileZone label="Brand Guidelines" accept=".pdf,.zip" icon={<FileText className="w-5 h-5"/>}
          value={data.brandGuidelinesName} onChange={v => u("brandGuidelinesName", v)}/>
        <FileZone label="Floor Plan / Technical Drawing" accept=".pdf,.dwg,.dxf,.png,.jpg" icon={<Ruler className="w-5 h-5"/>}
          value={data.floorPlanName} onChange={v => u("floorPlanName", v)}/>
        <FileZone label="Reference Images / Inspiration" accept="image/*,.pdf" icon={<ImageIcon className="w-5 h-5"/>} multiple
          value={data.referenceImages?.length > 0 ? data.referenceImages : null} onChange={v => u("referenceImages", Array.isArray(v) ? v : [v])}/>
        <FileZone label="Existing Stand Photos (if any)" accept="image/*" icon={<ImageIcon className="w-5 h-5"/>} multiple
          value={data.existingPhotos?.length > 0 ? data.existingPhotos : null} onChange={v => u("existingPhotos", Array.isArray(v) ? v : [v])}/>
      </div>
    </div>
  );
}

function StepBudget({ data, onChange }) {
  const u = (k, v) => onChange({ ...data, [k]: v });
  const { currencies } = useDropdown();
  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3"><DollarSign className="inline w-3 h-3 mr-1"/>Design Budget *</p>
          <div className="grid grid-cols-2 gap-2">
            {BUDGET_RANGES.map(r => (
              <button key={r.id} type="button" onClick={() => u("design", r.id)}
                className={`rounded-xl border-2 px-3 py-2.5 text-sm font-semibold transition-all ${data.design === r.id ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3"><Package className="inline w-3 h-3 mr-1"/>Build / Production Budget *</p>
          <div className="grid grid-cols-2 gap-2">
            {BUDGET_RANGES.map(r => (
              <button key={r.id} type="button" onClick={() => u("build", r.id)}
                className={`rounded-xl border-2 px-3 py-2.5 text-sm font-semibold transition-all ${data.build === r.id ? "border-purple-500 bg-purple-50 text-purple-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Currency</label>
          <select value={data.currency} onChange={e => u("currency", e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white">
            {currencies.length > 0
              ? currencies.map(c => <option key={c.code} value={c.currency_code}>{c.flag} {c.currency_code} — {c.currency_name}</option>)
              : ["EUR","USD","GBP","AED","TRY"].map(c => <option key={c}>{c}</option>)
            }
          </select>
        </div>
        <div className="md:col-span-2">
          <DatePickerField
            label="Decision Deadline"
            icon={<Clock className="inline w-3 h-3" />}
            value={data.deadline}
            onChange={(v) => u("deadline", v)}
          />
        </div>
      </div>
      <Toggle value={data.multipleShows} onChange={v => u("multipleShows", v)} label="This brief covers multiple shows" icon={<Globe className="w-5 h-5"/>}/>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Additional Notes & Special Requests</label>
        <textarea value={data.notes} onChange={e => u("notes", e.target.value)}
          rows={4} placeholder="Sustainability requirements, logistic constraints, special conditions..."
          className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none"/>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 w-36 shrink-0 mt-0.5">{label}</span>
      <span className="text-sm text-slate-800 font-medium">{value}</span>
    </div>
  );
}

function StepSummary({ allData }) {
  const { event, stand, technical, features, design, assets, budget } = allData;
  const sqm = (parseFloat(stand.widthM)||0) * (parseFloat(stand.depthM)||0);
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-blue-500"/>Event Information</h3>
        <SummaryRow label="Show"     value={event.showName}/>
        <SummaryRow label="Sector"   value={event.sector}/>
        <SummaryRow label="Location" value={[event.venue, event.city, event.country].filter(Boolean).join(", ")}/>
        <SummaryRow label="Dates"    value={event.startDate ? `${event.startDate} → ${event.endDate}` : null}/>
        <SummaryRow label="Stand No" value={event.hallLocation}/>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2"><LayoutGrid className="w-4 h-4 text-blue-500"/>Stand Details</h3>
        <SummaryRow label="Type"          value={STAND_TYPES.find(t => t.id === stand.type)?.label}/>
        <SummaryRow label="Size"          value={stand.widthM && stand.depthM ? `${stand.widthM}m × ${stand.depthM}m = ${sqm.toFixed(1)} m²` : null}/>
        <SummaryRow label="Height"        value={stand.heightM ? `${stand.heightM} m` : null}/>
        <SummaryRow label="Ceiling"       value={CEILING_TYPES.find(c => c.id === stand.ceilingType)?.label}/>
        <SummaryRow label="Double Storey" value={stand.doubleStorey ? "Yes" : null}/>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2"><Settings className="w-4 h-4 text-blue-500"/>Technical & Features</h3>
        <SummaryRow label="Flooring"      value={technical.raisedFlooring ? `Raised${technical.raisedMm ? ` (${technical.raisedMm}mm)` : ""}` : null}/>
        <SummaryRow label="Electrical"    value={technical.electrical}/>
        <SummaryRow label="Water / Drain" value={technical.water || technical.drains ? "Yes" : null}/>
        <SummaryRow label="Rigging"       value={technical.rigging ? "Yes" : null}/>
        <SummaryRow label="Internet"      value={technical.internet !== "none" ? technical.internet : null}/>
        <SummaryRow label="Meeting Rooms" value={features.meetingRooms > 0 ? `${features.meetingRooms}` : null}/>
        <SummaryRow label="Kitchen"       value={features.kitchen ? "Yes" : null}/>
        <SummaryRow label="AV Equipment"  value={features.av?.length > 0 ? features.av.join(", ") : null}/>
        <SummaryRow label="Lighting"      value={features.lighting?.length > 0 ? features.lighting.join(", ") : null}/>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2"><Palette className="w-4 h-4 text-blue-500"/>Design & Budget</h3>
        <SummaryRow label="Style"          value={DESIGN_STYLES.find(s => s.id === design.style)?.label}/>
        <SummaryRow label="Primary Colors" value={design.primaryColors?.length > 0 ? (
          <div className="flex gap-1">{design.primaryColors.map(c => <div key={c} className="w-5 h-5 rounded-full border border-slate-300" style={{backgroundColor: c}} title={c}/>)}</div>
        ) : null}/>
        <SummaryRow label="Materials"      value={design.materials?.length > 0 ? design.materials.join(", ") : null}/>
        <SummaryRow label="Files"          value={[assets.logoName, assets.floorPlanName, ...(assets.referenceImages||[])].filter(Boolean).join(", ") || null}/>
        <SummaryRow label="Design Budget"  value={budget.design ? `${BUDGET_RANGES.find(r=>r.id===budget.design)?.label} (${budget.currency})` : null}/>
        <SummaryRow label="Build Budget"   value={budget.build ? BUDGET_RANGES.find(r=>r.id===budget.build)?.label : null}/>
        <SummaryRow label="Deadline"       value={budget.deadline}/>
      </div>
    </div>
  );
}

function StepAuth({ allData, onSuccess }) {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  // Always start with "choose" — only switch to "submit" when user is confirmed
  const [mode, setMode] = useState("choose");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Guest form state - hoisted outside conditionals to comply with React Rules of Hooks
  const [guestForm, setGuestForm] = useState({ name: "", email: "", company: "" });
  const [guestSent, setGuestSent] = useState(false);

  // Sync mode with auth state (fixes race condition where user loads after mount)
  useEffect(() => {
    if (user) setMode("submit");
  }, [user]);

  const buildPayload = () => {
    const { event, stand, technical, features, design, assets, budget } = allData;
    const sqm = (parseFloat(stand.widthM)||0) * (parseFloat(stand.depthM)||0);
    return {
      event: { country: event.country || "", city: event.city || "", show: event.showName || "", month: event.startDate?.slice(0,7) || "" },
      stand: { w_m: parseFloat(stand.widthM)||0, d_m: parseFloat(stand.depthM)||0, indoor: true },
      budget: { min: 0, max: 0, currency: budget.currency || "EUR" },
      deadline_at: budget.deadline || new Date(Date.now() + 30*24*60*60*1000).toISOString().slice(0,10),
      notes: [
        `STAND: ${STAND_TYPES.find(t=>t.id===stand.type)?.label || ""} — ${sqm}m² — ${stand.heightM || "?"}m height`,
        `CEILING: ${CEILING_TYPES.find(c=>c.id===stand.ceilingType)?.label || ""}`,
        stand.doubleStorey ? "DOUBLE STOREY: Yes" : "",
        technical.raisedFlooring ? `FLOORING: Raised ${technical.raisedMm || ""}mm` : "",
        technical.rigging ? "RIGGING: Yes" : "",
        features.meetingRooms > 0 ? `MEETING ROOMS: ${features.meetingRooms}` : "",
        features.av?.length ? `AV: ${features.av.join(", ")}` : "",
        design.style ? `STYLE: ${design.style}` : "",
        design.primaryColors?.length ? `COLORS: ${design.primaryColors.join(", ")}` : "",
        design.materials?.length ? `MATERIALS: ${design.materials.join(", ")}` : "",
        design.designNotes || "",
        budget.notes || "",
        assets.logoName ? `LOGO: ${assets.logoName}` : "",
        assets.floorPlanName ? `FLOOR PLAN: ${assets.floorPlanName}` : "",
      ].filter(Boolean).join("\n"),
      wizard_data: allData,
    };
  };

  const doSubmit = async (token) => {
    setLoading(true); setError(null);
    try {
      const payload = buildPayload();
      const res = await axios.post(`${API}/exhibitor/briefs`, payload, { headers: { Authorization: `Bearer ${token}` } });
      await axios.patch(`${API}/exhibitor/briefs/${res.data.brief_id}/status`, { status: "OPEN" }, { headers: { Authorization: `Bearer ${token}` } });
      onSuccess(res.data.brief_id);
    } catch (e) {
      setError(e.response?.data?.detail || t('validation.submissionFailed'));
    } finally {
      setLoading(false);
    }
  };

  const doLogin = async () => {
    setLoading(true); setError(null);
    try {
      const result = await login(loginForm.email, loginForm.password);
      if (!result.success) { setError(result.error || t('validation.loginFailed')); setLoading(false); return; }
      if (result.user?.role !== "EXHIBITOR_ADMIN") { setError(t('validation.wrongAccountType')); setLoading(false); return; }
      const token = localStorage.getItem("access_token") || result.user?.access_token;
      await doSubmit(token);
    } catch (e) {
      setError(t('validation.loginFailed'));
      setLoading(false);
    }
  };

  if (mode === "submit" && user) {
    const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-green-200 bg-green-50 p-5 flex gap-4 items-center">
          <CheckCircle2 className="w-8 h-8 text-green-600 shrink-0"/>
          <div>
            <p className="font-bold text-green-800">You are signed in</p>
            <p className="text-sm text-green-700">Brief will be submitted as <strong>{user.email}</strong></p>
          </div>
        </div>
        {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
        <button type="button" disabled={loading} onClick={() => doSubmit(token || user.access_token)}
          data-testid="submit-brief-final"
          className="w-full flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-6 py-4 text-lg font-bold text-white shadow hover:bg-blue-700 disabled:opacity-60 transition-colors">
          {loading ? <><Loader2 className="w-5 h-5 animate-spin"/>Submitting...</> : <><CheckCircle2 className="w-5 h-5"/>Submit Brief & Get Quotes</>}
        </button>
      </div>
    );
  }

  if (mode === "choose") {
    return (
      <div className="space-y-4">
        <p className="text-slate-600 text-sm">You need an account to save your brief. How would you like to proceed?</p>
        <div className="grid gap-4 md:grid-cols-3">
          <button type="button" onClick={() => setMode("login")}
            className="flex flex-col items-center gap-3 rounded-2xl border-2 border-blue-200 bg-blue-50 p-6 hover:border-blue-500 transition-all group">
            <LogIn className="w-8 h-8 text-blue-600 group-hover:scale-110 transition-transform"/>
            <span className="font-bold text-slate-900">Sign In</span>
            <span className="text-xs text-slate-500 text-center">I have an existing Exhibitor account</span>
          </button>
          <button type="button" onClick={() => { localStorage.setItem("wizard_data", JSON.stringify(allData)); navigate("/exhibitor/signup"); }}
            className="flex flex-col items-center gap-3 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6 hover:border-emerald-500 transition-all group">
            <UserPlus className="w-8 h-8 text-emerald-600 group-hover:scale-110 transition-transform"/>
            <span className="font-bold text-slate-900">Register</span>
            <span className="text-xs text-slate-500 text-center">Create a new Exhibitor account</span>
          </button>
          <button type="button" onClick={() => setMode("guest")}
            className="flex flex-col items-center gap-3 rounded-2xl border-2 border-slate-200 bg-white p-6 hover:border-slate-400 transition-all group">
            <User className="w-8 h-8 text-slate-400 group-hover:scale-110 transition-transform"/>
            <span className="font-bold text-slate-900">Continue as Guest</span>
            <span className="text-xs text-slate-500 text-center">Submit without creating an account</span>
          </button>
        </div>
      </div>
    );
  }

  if (mode === "login") {
    return (
      <div className="space-y-4 max-w-sm mx-auto">
        <button type="button" onClick={() => setMode("choose")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft className="w-4 h-4"/> Back
        </button>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Email</label>
          <input type="email" value={loginForm.email} onChange={e => setLoginForm(f => ({...f, email: e.target.value}))}
            placeholder="email@company.com" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Password</label>
          <input type="password" value={loginForm.password} onChange={e => setLoginForm(f => ({...f, password: e.target.value}))}
            placeholder="••••••••" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        <button type="button" disabled={loading} onClick={doLogin}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-bold text-white hover:bg-blue-700 disabled:opacity-60">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin"/>Signing in...</> : <><LogIn className="w-4 h-4"/>Sign In & Submit Brief</>}
        </button>
      </div>
    );
  }

  if (mode === "guest") {
    const submitGuest = async () => {
      if (!guestForm.name || !guestForm.email) { setError(t('validation.nameEmailRequired')); return; }
      setLoading(true); setError(null);
      try {
        const { event, stand, design, budget } = allData;
        await axios.post(`${API}/public/inquiry`, {
          inquiry_type: "brief", target_type: "contractor", target_slug: "expostand-portal",
          name: guestForm.name, email: guestForm.email, company: guestForm.company,
          show_name: event.showName, city: event.city, budget: budget.design || "",
          notes: `WIZARD BRIEF: ${JSON.stringify({ stand, design, budget: budget.build }).slice(0, 2000)}`,
        });
        setGuestSent(true);
      } catch { setError(t('validation.submissionFailed')); }
      finally { setLoading(false); }
    };
    if (guestSent) return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-8 text-center">
        <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3"/>
        <h3 className="font-bold text-green-800 text-lg">Brief Received!</h3>
        <p className="text-green-700 mt-1">We will get in touch with you as soon as possible.</p>
      </div>
    );
    return (
      <div className="space-y-4 max-w-sm mx-auto">
        <button type="button" onClick={() => setMode("choose")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft className="w-4 h-4"/> Back
        </button>
        {[{label:"Full Name *", key:"name", type:"text"}, {label:"Email *", key:"email", type:"email"}, {label:"Company", key:"company", type:"text"}].map(f => (
          <div key={f.key}>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{f.label}</label>
            <input type={f.type} value={guestForm[f.key]} onChange={e => setGuestForm(g => ({...g, [f.key]: e.target.value}))}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
        ))}
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        <button type="button" disabled={loading} onClick={submitGuest}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-slate-800 px-4 py-3 font-bold text-white hover:bg-slate-900 disabled:opacity-60">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin"/>Submitting...</> : "Submit as Guest"}
        </button>
      </div>
    );
  }
  return null;
}

// ─── Main Wizard ───────────────────────────────────────────────────────────────

export default function ExhibitorBriefWizard({ portalMode = false }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL);
  const [submitted, setSubmitted] = useState(false);
  const [briefId, setBriefId] = useState(null);
  const [stepError, setStepError] = useState(null);
  const topRef = useRef();

  const updateStep = useCallback((key, val) => {
    setData(d => ({ ...d, [key]: val }));
  }, []);

  const canNext = () => {
    if (step === 1) return data.event.showName.trim() !== "";
    if (step === 2) return data.stand.type !== "" && data.stand.widthM !== "" && data.stand.depthM !== "";
    if (step === 7) return data.budget.design !== "" && data.budget.build !== "";
    return true;
  };

  const handleFillTestData = () => {
    setData({
      event: {
        showName: 'Hannover Messe 2025',
        sector: 'IT / Technology',
        country: 'DE',
        city: 'Hannover',
        venue: 'Messe Hannover',
        startDate: '2025-04-07',
        endDate: '2025-04-11',
        hallLocation: 'Hall 7, Stand B-23',
      },
      stand: {
        type: 'corner',
        widthM: '6',
        depthM: '4',
        heightM: '3',
        ceilingType: 'open',
        doubleStorey: false,
      },
      technical: {
        raisedFlooring: true,
        raisedMm: '100',
        electrical: 'high',
        electricalKw: '32',
        water: true,
        drains: false,
        compressedAir: false,
        rigging: true,
        internet: 'dedicated',
      },
      features: {
        receptionDesk: true,
        receptionCount: 2,
        meetingRooms: 1,
        storage: true,
        kitchen: false,
        stage: false,
        displayZones: 3,
        lighting: ['ambient', 'spot', 'led_strip'],
        av: ['lcd_screens', 'sound'],
      },
      design: {
        style: 'modern',
        primaryColors: ['#0f172a', '#2563eb'],
        secondaryColors: ['#ffffff', '#f1f5f9'],
        materials: ['glass', 'wood'],
        mood: 'Professional and technological atmosphere, clean lines, open space feel. Minimalist but prestigious.',
        designNotes: 'Brand colors are navy blue and white. We want an open stand design with high ceilings. LED lighting with product display areas is important. Reception desk near the front, enclosed meeting room at the back.',
      },
      assets: {
        logoName: 'TechCorp_Logo_2024.png',
        logoData: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?q=80&w=200&auto=format',
        brandGuidelinesName: 'TechCorp_Brand_Guidelines_v3.pdf',
        floorPlanName: 'Hannover_Hall7_FloorPlan_B23.pdf',
        referenceImages: [
          'stand_inspiration_minimal_2024.jpg',
          'stand_reference_corporate_tech.jpg',
          'stand_modern_glass_design.jpg',
        ],
        existingPhotos: [
          'previous_stand_Dubai_Gitex_2023.jpg',
          'previous_stand_Frankfurt_2022.jpg',
        ],
      },
      budget: {
        design: '10_25k',
        build: '25_50k',
        currency: 'EUR',
        deadline: '2025-03-01',
        multipleShows: false,
        notes: 'Deadline is critical — installation must be completed by Sunday evening. Installation access required 1 day before the show starts. Dismantling within 3 days.',
      },
    });
  };

  const next = () => {
    if (!canNext()) {
      setStepError(t('validation.fillAllRequired'));
      return;
    }
    setStepError(null);
    if (step < 9) { setStep(s => s + 1); topRef.current?.scrollIntoView({behavior:"smooth"}); }
  };
  const back = () => { setStepError(null); if (step > 1) { setStep(s => s - 1); topRef.current?.scrollIntoView({behavior:"smooth"}); }};

  if (submitted) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-50 flex items-center justify-center px-4">
      <motion.div initial={{scale:0.8, opacity:0}} animate={{scale:1, opacity:1}} className="max-w-md w-full rounded-3xl border border-green-200 bg-white p-10 text-center shadow-xl">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-10 h-10 text-green-600"/>
        </div>
        <h1 className="text-2xl font-black text-slate-900">Brief Submitted!</h1>
        <p className="mt-3 text-slate-600">Your brief has been successfully created. Suitable contractors will submit their quotes shortly.</p>
        {briefId && <p className="mt-2 text-xs text-slate-400 font-mono">Brief ID: {briefId}</p>}
        <div className="mt-8 flex flex-col gap-3">
          <button onClick={() => navigate("/app/exhibitor/proposals")} className="rounded-2xl bg-blue-600 py-3 font-bold text-white hover:bg-blue-700">
            Track Quotes
          </button>
          <button onClick={() => navigate("/app/exhibitor/dashboard")} className="rounded-2xl border border-slate-200 py-3 font-semibold text-slate-700 hover:bg-slate-50">
            Go to Dashboard
          </button>
        </div>
      </motion.div>
    </div>
  );

  const StepIcon = STEPS[step - 1].icon;
  const progress = ((step - 1) / (STEPS.length - 1)) * 100;

  const STEP_CONTENT = [
    <StepEvent     data={data.event}    onChange={v => updateStep("event", v)}/>,
    <StepStand     data={data.stand}    onChange={v => updateStep("stand", v)}/>,
    <StepTechnical data={data.technical}onChange={v => updateStep("technical", v)}/>,
    <StepFeatures  data={data.features} onChange={v => updateStep("features", v)}/>,
    <StepDesign    data={data.design}   onChange={v => updateStep("design", v)}/>,
    <StepAssets    data={data.assets}   onChange={v => updateStep("assets", v)}/>,
    <StepBudget    data={data.budget}   onChange={v => updateStep("budget", v)}/>,
    <StepSummary   allData={data}/>,
    <StepAuth      allData={data} onSuccess={(id) => { setBriefId(id); setSubmitted(true); }}/>,
  ];

  return (
    <div className={portalMode ? "bg-slate-50" : "min-h-screen bg-slate-50"} ref={topRef}>
      {/* Show public navbar only in public mode */}
      {!portalMode && <PublicNavbar />}

      {/* Wizard Step Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => portalMode ? navigate("/app/exhibitor/briefs") : navigate("/exhibitor/landing")}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
                <ArrowLeft className="w-4 h-4"/> Back
              </button>
              <button
                type="button"
                onClick={handleFillTestData}
                title="Fill all steps with demo data"
                className="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg px-2.5 py-1.5 font-semibold transition-all"
                data-testid="btn-fill-brief-demo"
              >
                <Wand2 className="w-3.5 h-3.5"/> Fill Demo
              </button>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Create Brief</p>
              <p className="text-sm font-bold text-slate-900">{STEPS[step-1].label}</p>
            </div>
            <p className="text-xs font-semibold text-slate-400">{step} / {STEPS.length}</p>
          </div>
          {/* Progress bar */}
          <div className="relative h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <motion.div className="absolute inset-y-0 left-0 bg-blue-600 rounded-full" animate={{ width: `${progress}%` }} transition={{ type: "spring", stiffness: 300, damping: 30 }}/>
          </div>
          {/* Step dots */}
          <div className="flex justify-between mt-2.5">
            {STEPS.map(s => {
              const Icon = s.icon;
              const done = s.id < step;
              const active = s.id === step;
              return (
                <button key={s.id} onClick={() => s.id < step && setStep(s.id)} title={s.label}
                  className={`flex flex-col items-center gap-0.5 transition-all ${s.id > step ? "opacity-30" : ""}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                    done ? "bg-blue-600" : active ? "bg-blue-600 ring-4 ring-blue-100" : "bg-slate-200"
                  }`}>
                    {done ? <CheckCircle2 className="w-3.5 h-3.5 text-white"/> : <Icon className={`w-3 h-3 ${active ? "text-white" : "text-slate-400"}`}/>}
                  </div>
                  <span className={`text-[9px] font-semibold hidden sm:block ${active ? "text-blue-600" : done ? "text-blue-400" : "text-slate-300"}`}>{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }}>
            {/* Step Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center shadow-md">
                <StepIcon className="w-6 h-6 text-white"/>
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900">{STEPS[step-1].label}</h2>
                <p className="text-sm text-slate-500">Step {step} of {STEPS.length}</p>
              </div>
            </div>
            {STEP_CONTENT[step - 1]}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        {step < 9 && (
          <>
            {stepError && (
              <div className="mt-4 flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                <span className="font-semibold">⚠</span> {stepError}
              </div>
            )}
          <div className="flex items-center justify-between mt-4 pt-6 border-t border-slate-200">
            <button type="button" onClick={back} disabled={step === 1}
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-30 transition-all">
              <ArrowLeft className="w-4 h-4"/> Back
            </button>
            <button type="button" onClick={next}
              data-testid={`wizard-next-step-${step}`}
              className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 font-bold text-white hover:bg-blue-700 transition-all shadow-md">
              {step === 7 ? "View Summary" : step === 8 ? "Account Step" : "Next"} <ArrowRight className="w-4 h-4"/>
            </button>
          </div>
          </>
        )}
      </div>

      {/* Standard Site Footer — hidden in portal mode */}
      {!portalMode && <PublicFooter />}
    </div>
  );
}
